import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useGameplayConfig } from 'store';
import { LanguageSelect } from 'components/common/LanguageSelect';
import { saveLanguage } from 'store/translation/actions';
import { useIsMobileDevice } from 'hooks';
import analytics from 'services/config/analytics';
import styles from './style.module.scss';

const LanguageSelectionScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: linkId } = useParams();
  const isMobile = useIsMobileDevice();
  
  const {
    languagesOptions,
    customizableForm,
    configLanguageSettings
  } = useGameplayConfig();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(false);

  const redirectToNext = useCallback(() => {
    const nextPath = customizableForm ? "form" : "rules";
    history.replace(`/${nextPath}/${linkId}`);
  }, [customizableForm, history, linkId]);

  useEffect(() => {
    if (!languagesOptions || languagesOptions.length <= 1) {
      redirectToNext();
      return;
    }
    
    if (configLanguageSettings?.configLanguage) {
      setHasSelectedLanguage(true);
    }

    analytics.track('language_selection_viewed');
  }, [languagesOptions, configLanguageSettings, redirectToNext]);

  useEffect(() => {
    if (!linkId) {
      history.replace('/');
    }
  }, [linkId, history]);

  const handleContinue = async () => {
    if (!hasSelectedLanguage || !configLanguageSettings?.configLanguage) {
      return;
    }

    setIsSubmitting(true);

    analytics.track('language_selected', {
      language: configLanguageSettings.configLanguage,
    });

    try {
      await saveLanguage(linkId, configLanguageSettings.configLanguage);

      redirectToNext();
    } catch (error) {
      console.error('Error saving language:', error);
      redirectToNext();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLanguageChange = () => {
    setHasSelectedLanguage(true);
  };

  if (!languagesOptions || languagesOptions.length <= 1) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {t("language_selection.title", "Select Your Language")}
          </h1>
          <p className={styles.subtitle}>
            {t("language_selection.subtitle", "Choose your preferred language to continue")}
          </p>
        </div>

        <div className={styles.languageSection}>
          <LanguageSelect
            visible={true}
            showLanguageLabel={false}
            className={styles.languageSelect}
            selectClassName="login-language-select"
            onLanguageChange={handleLanguageChange}
          />
        </div>

        <div className={styles.buttonSection}>
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={!hasSelectedLanguage}
            onClick={handleContinue}
            className={styles.continueButton}
          >
            {t("language_selection.continue", "Continue")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelectionScreen;
